'use client';

import React, { useState, useRef, useEffect } from 'react';
import dynamic from 'next/dynamic';
import { ImageField } from '@sitecore-jss/sitecore-jss-react';
import classNames from 'classnames';
import ImageWrapper, { SizedImageField } from 'components/helpers/ImageWrapper/ImageWrapper';

// Dynamically import ReactPlayer without SSR
const ReactPlayer = dynamic(() => import('react-player'), { ssr: false });

interface VideoComponentProps {
  desktopVideoUrl: string;
  mobileVideoUrl: string;
  stopImageDesktop: ImageField;
  stopImageMobile: ImageField;
  loopCount?: number;
  screenWidth: number;
  infiniteLoop: boolean;
}

const VideoPlayer: React.FC<VideoComponentProps> = ({
  desktopVideoUrl,
  mobileVideoUrl,
  stopImageDesktop,
  stopImageMobile,
  loopCount = 0,
  infiniteLoop = false,
  screenWidth,
}) => {
  const playerRef = useRef<any>(null);
  const [playCount, setPlayCount] = useState(0);
  const [showStopImage, setShowStopImage] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [isMobile, setIsMobile] = useState(screenWidth <= 575);

  const videoUrl = isMobile ? mobileVideoUrl : desktopVideoUrl;
  const stopImage = isMobile ? stopImageMobile : stopImageDesktop;
  const heightClass = isMobile ? 'h-[400px]' : 'h-[700px]';

  useEffect(() => {
    setPlaying(true);
    setIsMounted(true);
  }, []);

  // Effect to handle window resize events
  useEffect(() => {
    setIsMobile(window.innerWidth <= 575);
    const handleResize = () => setIsMobile(window.innerWidth <= 575);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobile]);

  function handleEnded() {
    const nextCount = playCount + 1;
    if (infiniteLoop || nextCount < loopCount) {
      setPlayCount(nextCount);
      if (playerRef.current) {
        playerRef.current?.retry();
      }
    } else {
      setShowStopImage(true);
      setPlaying(false);
    }
  }

  return (
    <>
      {isMounted && !showStopImage ? (
        <div
          className={classNames(
            'video-player',
            'relative', // needed for aspect ratio
            'w-full', // full width
            'pt-[56.25%]' // 16:9 aspect ratio
          )}
        >
          <ReactPlayer
            className={classNames(
              'bcbsaz-video-player',
              'absolute',
              'top-0',
              'left-0',
              'w-full',
              'h-full'
            )}
            ref={playerRef}
            url={videoUrl}
            playing={playing}
            onEnded={handleEnded}
            onError={() => setShowStopImage(true)}
            controls={false}
            muted={true}
            width="100%"
            height="100%"
            playsinline
          />
        </div>
      ) : (
        <div className={classNames('video-player-cover', 'w-full')}>
          <ImageWrapper
            layout="responsive"
            field={stopImage as SizedImageField}
            imageClass={classNames(
              'video-player-cover-img',
              'w-full',
              '!h-full',
              'object-cover',
              heightClass
            )}
          />
        </div>
      )}
    </>
  );
};

export default VideoPlayer;
